/**
 * Let go of documents no window is looking at (RFC 0317 §3b).
 *
 * THE FAILURE. Before one restart the app's shared worker held 246 documents
 * at 49 million ops. Three were single sessions at five to six million ops
 * each. Every session opened during the day stayed loaded, because a document
 * a tab asks for is loaded into the worker and nothing ever lets it go: wasm
 * memory never shrinks, so by the afternoon the laptop was paging.
 *
 * `evictRotatedPresence` was the first eviction the worker ever had, for one
 * shape of document. This is the general one. The worker does not know which
 * documents a window is looking at, only which it has asked for, and a tab
 * that opened a document once and is still showing it makes no further
 * requests. So each tab REPORTS what it holds, on an interval, and a document
 * no tab has reported for `idleMs` is a candidate. A tab that stops reporting
 * (the window closed) drops out after `reportTtlMs`, and what only it held
 * goes idle with it.
 *
 * EVICTION IS NOT DELETION. `removeFromCache` drops the handle and detaches
 * the sync sources; storage keeps the bytes and a later `find` reloads them.
 * Pending writes are flushed first, so nothing a tab wrote is lost between
 * the write and the sweep.
 */

import * as A from '@automerge/automerge/slim'
import type {
  DocumentId,
  PeerId,
  Repo,
  RequestMessage,
} from '@automerge/automerge-repo/slim'

/** A document nobody has reported for this long is let go of. */
export const IDLE_EVICT_MS = 5 * 60_000
/** How often to look. Same cadence as the presence sweep. */
export const IDLE_EVICT_INTERVAL_MS = 5 * 60_000
/** How often a tab reports what it holds. */
export const HELD_REPORT_INTERVAL_MS = 30_000
/** A tab silent for this long has gone; its report no longer counts. */
export const HELD_REPORT_TTL_MS = 3 * HELD_REPORT_INTERVAL_MS

export interface IdleEvictionPolicy {
  /** Documents the worker keeps regardless of who holds them. */
  pinned?: () => Iterable<string>
  idleMs?: number
  intervalMs?: number
  reportTtlMs?: number
  now?: () => number
}

/**
 * Who holds what, as reported. Pure: the sweep asks it for the candidates and
 * it never touches the repo.
 */
export class HeldDocuments {
  private readonly reports = new Map<string, { ids: Set<string>; at: number }>()
  /** When each document was last held by anyone, or first seen loaded. */
  private readonly lastHeld = new Map<string, number>()

  constructor(
    private readonly opts: {
      idleMs: number
      reportTtlMs: number
    },
  ) {}

  /** A tab says what it holds now. Replaces its previous report. */
  report(peer: string, ids: Iterable<string>, now: number): void {
    const set = new Set(ids)
    this.reports.set(peer, { ids: set, at: now })
    for (const id of set) this.lastHeld.set(id, now)
  }

  /** A tab is known to be gone. */
  forget(peer: string): void {
    this.reports.delete(peer)
  }

  /** Every document some live tab holds. */
  held(now: number): Set<string> {
    const out = new Set<string>()
    for (const [peer, r] of this.reports) {
      if (now - r.at > this.opts.reportTtlMs) {
        this.reports.delete(peer)
        continue
      }
      for (const id of r.ids) out.add(id)
    }
    return out
  }

  /**
   * Among `loaded`, the documents no live tab holds, not pinned, and idle for
   * longer than `idleMs`. A document seen loaded for the first time starts its
   * idle clock now, so a fresh load is never swept in the same tick.
   */
  candidates(
    loaded: Iterable<string>,
    pinned: Iterable<string>,
    now: number,
  ): string[] {
    const held = this.held(now)
    const keep = new Set(pinned)
    const out: string[] = []
    const seen = new Set<string>()
    for (const id of loaded) {
      seen.add(id)
      if (held.has(id) || keep.has(id)) continue
      const last = this.lastHeld.get(id)
      if (last === undefined) {
        this.lastHeld.set(id, now)
        continue
      }
      if (now - last > this.opts.idleMs) out.push(id)
    }
    // A document the repo no longer holds has no clock to keep.
    for (const id of this.lastHeld.keys()) {
      if (!seen.has(id)) this.lastHeld.delete(id)
    }
    for (const id of out) this.lastHeld.delete(id)
    return out
  }
}

function loadedIds(repo: Repo): string[] {
  const handles = Object.keys(
    (repo as unknown as { handles: Record<string, unknown> }).handles ?? {},
  )
  return handles.map(String)
}

/**
 * Flush what a document has pending, then drop its handle.
 *
 * ON A REPO WITH NO STORAGE (the window's tab repo) this is only half of an
 * eviction. The document can only come back by syncing again, and the peer
 * that serves it will not start one until it has been told to forget this
 * repo's copy: `forgetPeerSync` on that side. Call this directly on a tab
 * repo and the next find hangs on `loading` for good. The app's held-docs
 * release path sends `released` for exactly this reason; go through it.
 */
export async function evictDocuments(
  repo: Repo,
  ids: readonly string[],
): Promise<void> {
  const r = repo as unknown as {
    flush?: (ids: string[]) => Promise<void>
    removeFromCache?: (id: string) => Promise<void>
  }
  if (!r.removeFromCache) return
  if (r.flush) await r.flush.call(repo, [...ids])
  for (const id of ids) await r.removeFromCache.call(repo, id)
}

type DocSyncs = Record<
  string,
  {
    hasPeer(peerId: string): boolean
    addPeer(
      peerId: string,
      syncState: Promise<undefined>,
      options?: { messages: RequestMessage[] },
    ): void
    removePeer(peerId: string): void
  }
>

function docSynchronizer(repo: Repo, documentId: string) {
  return (repo as unknown as { synchronizer?: { docSynchronizers?: DocSyncs } })
    .synchronizer?.docSynchronizers?.[documentId]
}

/**
 * Stop syncing `documentId` to `peerId`: the peer let it go.
 *
 * Re-adding the peer here instead (`addPeer`, the first version) pushed the
 * whole document back unasked, so the window's sweep evicted 300 documents
 * and got 300 back, every cycle (seen live, Oct 7). Dropped, the peer is
 * sent nothing until it asks again through `resyncPeer`.
 *
 * A no-op when this repo does not hold the document, or the peer is unknown
 * to it: there is nothing to stop.
 */
export function forgetPeerSync(
  repo: Repo,
  documentId: string,
  peerId: string,
): boolean {
  const ds = docSynchronizer(repo, documentId)
  if (!ds?.hasPeer(peerId)) return false
  ds.removePeer(peerId)
  return true
}

/**
 * Send `documentId` to `peerId` whole: the peer evicted it and wants it back.
 *
 * A peer with no storage (the window's tab repo) can only get an evicted
 * document back by syncing again, and its own re-find sends nothing, so the
 * query stays loading for good (seen live, 2026-09-19). The fork's
 * DocSynchronizer says what to do when a peer's connection is effectively
 * new: `addPeer` from an empty sync state, which sends the whole document.
 *
 * A no-op when this repo does not hold the document.
 */
export function resyncPeer(
  repo: Repo,
  documentId: string,
  peerId: string,
): boolean {
  const ds = docSynchronizer(repo, documentId)
  if (!ds) return false
  // `wanted` is an explicit request from an empty tab. addPeer alone resets
  // hasRequested, so an announce=false policy sends nothing on the second
  // open. Queue the empty peer's handshake as a request; access policy still
  // decides whether the document may be sent.
  const empty = A.init()
  try {
    const [, data] = A.generateSyncMessage(empty, A.initSyncState())
    if (!data) return false
    ds.addPeer(peerId, Promise.resolve(undefined), {
      messages: [
        {
          type: 'request',
          documentId: documentId as DocumentId,
          senderId: peerId as PeerId,
          targetId: repo.peerId,
          data,
        },
      ],
    })
  } finally {
    A.free(empty)
  }
  return true
}

export interface IdleEviction {
  /** A tab says what it holds. */
  report(peer: string, ids: Iterable<string>): void
  /** A tab is gone. */
  forget(peer: string): void
  /** One sweep, now. Returns what was evicted. */
  tick(): Promise<string[]>
  stop(): void
}

/**
 * Evict on an interval. `onEvict` is the only report: a sweep that finds
 * nothing is silent.
 */
export function sweepIdleDocuments(
  repo: Repo,
  opts: IdleEvictionPolicy & { onEvict?: (ids: string[]) => void } = {},
): IdleEviction {
  const now = opts.now ?? Date.now
  const held = new HeldDocuments({
    idleMs: opts.idleMs ?? IDLE_EVICT_MS,
    reportTtlMs: opts.reportTtlMs ?? HELD_REPORT_TTL_MS,
  })
  let inFlight = false
  const tick = async (): Promise<string[]> => {
    if (inFlight) return []
    inFlight = true
    try {
      const ids = held.candidates(loadedIds(repo), opts.pinned?.() ?? [], now())
      if (ids.length === 0) return []
      await evictDocuments(repo, ids)
      opts.onEvict?.(ids)
      return ids
    } catch {
      // A failed sweep is the next sweep's problem.
      return []
    } finally {
      inFlight = false
    }
  }
  const timer = setInterval(
    () => void tick(),
    opts.intervalMs ?? IDLE_EVICT_INTERVAL_MS,
  )
  if (typeof (timer as { unref?: () => void }).unref === 'function') {
    ;(timer as { unref: () => void }).unref()
  }
  return {
    report: (peer, ids) => held.report(peer, ids, now()),
    forget: (peer) => held.forget(peer),
    tick,
    stop() {
      clearInterval(timer)
    },
  }
}
